import TextField from "@mui/material/TextField";
import { useState, useEffect } from "react";
import { Box, Button, useTheme } from "@mui/material";
import { Cancel } from "@mui/icons-material";
import { useDebounce } from "../../hooks/utils/useDebounce";
import { iconFillColors } from "./icons/Icon.types";

const DEBOUNCE_SEARCH_TIME = 300;

type Props = {
  searchTerm: string;
  onValueChange: (value: string) => void;
  fullWidth?: boolean;
  ariaLabel?: string;
  placeholder?: string;
  onEnterPressed?: () => void;
  onClear?: () => void;
};

export const SearchBox = ({
  searchTerm,
  fullWidth,
  ariaLabel,
  placeholder = "Search",
  onValueChange,
  onEnterPressed,
  onClear,
}: Props) => {
  const theme = useTheme();
  const [search, setSearch] = useState(searchTerm);
  const onChangeSearch = useDebounce(onValueChange, DEBOUNCE_SEARCH_TIME);

  // Sync internal state with external searchTerm prop changes
  useEffect(() => {
    setSearch(searchTerm);
  }, [searchTerm]);
  return (
    <Box sx={{ position: "relative", flex: fullWidth ? "1 1" : "unset" }}>
      <TextField
        slotProps={{
          input: {
            "aria-label": ariaLabel || placeholder,
            sx: {
              backgroundColor: theme.palette.background.paper,
              width: fullWidth ? "100%" : "13.75rem",
            },
          },
          inputLabel: { shrink: true },
        }}
        sx={{
          flex: "0 100%",
          width: fullWidth ? "100%" : "13.75rem",
          maxWidth: fullWidth ? "100%" : "400px",
          "& input": {
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          },
          "& input::placeholder": {
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          },
        }}
        type="search"
        placeholder={placeholder}
        value={search}
        onChange={(event) => {
          const value = event.target.value;
          setSearch(value);
          onChangeSearch(value);
        }}
        onKeyUp={(event) => {
          if (event.key === "Enter") {
            onEnterPressed?.();
          }
        }}
      />
      {search && (
        <ClearButton
          onClick={() => {
            setSearch("");
            onChangeSearch("");
            onClear?.();
          }}
        />
      )}
    </Box>
  );
};

const ClearButton = ({ onClick }: { onClick?: () => void }) => (
  <Button
    sx={{
      position: "absolute",
      right: "0.5rem",
      top: "0.563rem",
      width: "1.5rem",
      minWidth: "1.5rem",
      height: "1.5rem",
      borderRadius: "100%",
    }}
    disableRipple
    onClick={onClick}
  >
    <Cancel sx={{ color: iconFillColors.darkGray }} />
  </Button>
);
